import { EventEmitter, Injectable, Output, signal } from "@angular/core";
import { MovilModel } from "../services/resources/models/movil.model";
import { GenericRepository } from "./generic-repository";
import { SubflotaModel } from "../services/flota/models/subflota.model";
import { HttpClient } from "@angular/common/http";
import { SsoRepository } from "./sso-repository";
import { FlotasRepository } from "./flotas.repository";
import { catchError, combineLatest, map, Observable, of, Subscription, switchMap, tap, throwError } from "rxjs";
import { ConfigRepository } from "./config.repository";
import { MainComponent } from "../components/main/main.component";
import { MovilesInterface } from "../services/resources/interfaces/moviles.interface";
import { PosicionModel } from "../services/positions/models/posicion.model";

@Injectable({
  providedIn: 'root'
})
export class MovilesRepository extends GenericRepository<MovilModel> {
  private subflotas = new Map<number, SubflotaModel>();

  public moviles = new Map<number, MovilModel>();
  public recordCountTotal = signal<number>(0);
  public recordCuntUpdate = signal<number>(0);

  @Output() public newMovilEmiter: EventEmitter<MovilModel> = new EventEmitter();
  @Output() public changeMovilEmiter: EventEmitter<MovilModel> = new EventEmitter();
  @Output() public changeFilterEmiter: EventEmitter<void> = new EventEmitter();

  public constructor(
    private http: HttpClient,
    private ssoRepo: SsoRepository,
    private flotasRepo: FlotasRepository,
    private configRepo: ConfigRepository
  ) {
    super();
  }

  public load(): Observable<MovilModel[]> {
    const apiUrl = this.ssoRepo.getTicket().UrlApi;
    const idEmpresa = this.ssoRepo.getTicket().Empresa.IdGestion;
    const urlMoviles = `${apiUrl}/api/moviles?empresaId=${idEmpresa}`;

    const subflotaResto = new SubflotaModel();
    subflotaResto.Id = 0;
    subflotaResto.Nombre = 'Resto flota';
    subflotaResto.MovilesId = [];

    return this.flotasRepo.fetch().pipe(
      tap(subflotasList => {
        this.subflotas.clear();
        subflotasList.forEach(subflota => {
          subflota.MovilesId?.forEach(movilId => {
            this.subflotas.set(movilId, subflota);
          });
        });
      }),
      switchMap(() => this.http.get<MovilModel[]>(urlMoviles)),
      tap((moviles) => {
        moviles.forEach(movil => {
          movil.Clase = movil.ConjuntoVehiculo?.Clases?.[0] ?? null;
          const oldMovil = this.moviles.get(movil.Codigo)
            ? { ...this.moviles.get(movil.Codigo) }
            : undefined;
          if (oldMovil) {
            movil.marker = oldMovil.marker;
            movil.ultimaPos = oldMovil.ultimaPos;
            movil.selec = oldMovil.selec;
            movil.Conectado = oldMovil.Conectado ?? 0;
            movil.Ignicion = oldMovil.Ignicion ?? 0;
          }
          movil.Subflota = this.subflotas.get(movil.Codigo) || subflotaResto;
          this.moviles.set(movil.Codigo, movil);
          if (!oldMovil) {
            this.newMovilEmiter.emit(movil);
          } else if (!MovilModel.compare(movil, oldMovil)) {
            this.changeMovilEmiter.emit(movil);
          }
        });
      }),
      catchError(err => {
        console.error('Error al obtener móviles o subflotas:', err);
        return throwError(() => err);
      })
    );
  }

  public fetch(all: boolean = false): Observable<MovilModel[]> {
    const isEco = MainComponent.getInstance().isEcoEvolution;

    const iaFilter$ = isEco
      ? this.configRepo.getEmp('moviles-IA', null).pipe(
          map(result => (result ? JSON.parse(result) : null)),
          map((filterArray: any) =>
            filterArray ? new Set(filterArray.map((s: any) => String(s.id))) : null
          )
        )
      : of(null);

    const visiblesFilter$ = this.configRepo.getUsuEmpApp('moviles-visibles', null).pipe(
      map(result => (result ? JSON.parse(result) : null)),
      map((filterArray: any) =>
        filterArray ? new Set(filterArray.map((s: any) => String(s.id))) : null
      )
    );

    return combineLatest([iaFilter$, visiblesFilter$]).pipe(
      switchMap(([iaFilterSet, visibleFilterSet]) =>
        this.load().pipe(
          map((moviles: MovilModel[]) => {
            return moviles.filter(movil => {
              const movilId = String(movil.Codigo);
              const isInIA = !iaFilterSet || iaFilterSet.has(movilId);
              const isVisible = !visibleFilterSet || visibleFilterSet.has(movilId);
              return all || (isInIA && isVisible);
            });
          })
        )
      )
    );
  }

  public getMovil(id: number): MovilModel {
    const movil = this.moviles.get(id);
    return movil !== undefined ? movil : null;
  }

  public setMovilPos(id: number, pos: PosicionModel): void {
    const movil = this.moviles.get(id);
    movil.ultimaPos = pos;
    this.moviles.set(id, movil);
  }

  public setMovilConexion(id: number, conex: boolean, igni: boolean): void {
    const oldMovil = { ...this.moviles.get(id) };
    const movil = this.moviles.get(id);
    movil.Conectado = conex ? 1 : 0;
    movil.Ignicion = igni ? 1 : 0;
    this.moviles.set(id, movil);
    if (oldMovil && !MovilModel.compare(movil, oldMovil)) {
      this.changeMovilEmiter.emit(movil);
    }
  }

  public subscribeNewMoviles(subscriber: MovilesInterface): Subscription {
      return this.newMovilEmiter.subscribe(movil => {
        subscriber.onNewMovil(movil);
      });
    }

  public subscribeChangeMoviles(subscriber: MovilesInterface): Subscription {
    return this.changeMovilEmiter.subscribe(movil => {
      subscriber.onChangeMovil(movil);
    });
  }

  public setFilterVisible(): void {
    this.changeFilterEmiter.emit();
  }

  public recordCountMovilesTotal(count: number): void {
    this.recordCountTotal.set(count);
  }

  public recordCountMovilesUpdate(count: number): void {
    this.recordCuntUpdate.set(count);
  }
}
